import { TableFilterConfig, FilterFieldConfig } from 'hooks';
import { LOCAL_STORAGE_KEYS } from 'consts';

/**
 * Filter fields for Document Summary (Utility Bills) table.
 * Column ids align with MRT column id / accessorKey. apiKey maps to API param names where they differ.
 */
export const DOCUMENT_SUMMARY_FILTER_FIELDS: FilterFieldConfig[] = [
  { id: 'customerSiteId', type: 'text', label: 'Customer Site ID' },
  { id: 'businessUnit', type: 'text', label: 'Business Unit' },
  { id: 'siteName', type: 'text', label: 'Site' },
  { id: 'type', type: 'text', label: 'Type' },
  { id: 'subtype', type: 'text', label: 'Sub-Type' },
  { id: 'provider', type: 'text', label: 'Source' },
  { id: 'accountNumber', type: 'text', label: 'Account Number' },
  { id: 'meterNumber', type: 'text', label: 'Meter Number' },
  { id: 'scope', type: 'text', label: 'GHG Emission' },
  { id: 'addedBy', type: 'text', label: 'Added By' },
  { id: 'consumptionStartDate', type: 'text', label: 'Start date' },
  { id: 'consumptionEndDate', type: 'text', label: 'End date' },
  { id: 'daysCovered', type: 'text', label: 'Number of Days' },
  { id: 'measuredValue', type: 'number', label: 'Usage' },
  { id: 'standardizedUsageValue', type: 'text', label: 'Standardized Usage Value' },
  { id: 'co2Emission', type: 'number', label: 'Location Based Emissions' },
  { id: 'locationBasedEmissions', type: 'text', label: 'Location Based Emissions' },
  { id: 'locationRenewableUsage', type: 'text', label: 'Location Renewable Usage' },
  { id: 'locationNonRenewableUsage', type: 'text', label: 'Location Non-Renewable Usage' },
  { id: 'marketBasedEmission', type: 'text', label: 'Market Based Emissions' },
  { id: 'marketRenewableUsage', type: 'text', label: 'Market Renewable Usage' },
  { id: 'marketNonRenewableUsage', type: 'text', label: 'Market Non-Renewable Usage' },
  { id: 'amount', type: 'number', label: 'Cost' },
  { id: 'createDate', type: 'text', label: 'Date Created' },
  { id: 'updateDate', type: 'text', label: 'Last Modified Date' },
  { id: 'calculationMethod', type: 'text', label: 'Calculation Method' },
  { id: 'manuallyApproved', type: 'text', label: 'Manually Approved' },
  { id: 'duplicateWarning', type: 'text', label: 'Duplicate Warning' },
  { id: 'thresholdExceededConsumptionLastPeriod', type: 'text', label: 'Sequential Threshold Consumption Last Period' },
  { id: 'thresholdExceededCostLastPeriod', type: 'text', label: 'Sequential Threshold Cost Last Period' },
  { id: 'thresholdExceededLastYear', type: 'text', label: 'Sequential Threshold Last Year' },
  { id: 'estimateSource', type: 'text', label: 'Estimate Source' },
  { id: 'unitCost', type: 'text', label: 'Unit Cost' },
  { id: 'usagePerUnitArea', type: 'text', label: 'Usage Per Unit Area' },
  { id: 'billSuperceeded', type: 'text', label: 'Bill Superseded', apiKey: "billSuperseded" },
  { id: 'id', type: 'text', label: 'Bill ID', apiKey: "billGuid" },
  { id: 'statusFilter', type: 'text', label: 'Data Entries Status', apiKey: "dataEntriesStatus" },
];

export interface DocumentSummaryFilters {
  customerSiteId: string | null;
  businessUnit: string | null;
  siteName: string | null;
  type: string | null;
  subtype: string | null;
  provider: string | null;
  accountNumber: string | null;
  meterNumber: string | null;
  scope: string | null;
  addedBy: string | null;
  consumptionStartDate: string | null;
  consumptionEndDate: string | null;
  daysCovered: string | null;
  measuredValue: string | null;
  standardizedUsageValue: string | null;
  co2Emission: string | null;
  locationBasedEmissions: string | null;
  locationRenewableUsage: string | null;
  locationNonRenewableUsage: string | null;
  marketBasedEmission: string | null;
  marketRenewableUsage: string | null;
  marketNonRenewableUsage: string | null;
  amount: string | null;
  createDate: string | null;
  updateDate: string | null;
  calculationMethod: string | null;
  manuallyApproved: string | null;
  duplicateWarning: string | null;
  thresholdExceededConsumptionLastPeriod: string | null;
  thresholdExceededCostLastPeriod: string | null;
  thresholdExceededLastYear: string | null;
  estimateSource: string | null;
  unitCost: string | null;
  usagePerUnitArea: string | null;
  billSuperceeded: string | null;
  id: string | null;
  statusFilter: string | null;
}

export const createDocumentSummaryFilterConfig = (): TableFilterConfig<DocumentSummaryFilters> => {
  const columnIdMap = DOCUMENT_SUMMARY_FILTER_FIELDS.reduce<Record<string, string>>((acc, f) => {
    acc[f.id] = f.id;
    return acc;
  }, {});

  return {
    localStorageKey: LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_COLUMN_FILTERS,
    filterMode: 'column',
    fields: DOCUMENT_SUMMARY_FILTER_FIELDS,
    displayNameMap: Object.fromEntries(
      DOCUMENT_SUMMARY_FILTER_FIELDS.map((f) => [f.id, f.label])
    ) as Partial<Record<keyof DocumentSummaryFilters, string>>,
    columnIdMap,
  };
};
